import { AddLarge } from '@carbon/icons-react';
import uniq from 'lodash/uniq';
import * as React from 'react';
import {
  ChipInput,
  InputButton,
  InputWrapper,
  Radio,
} from 'vet-bones/design-system/components';

import {
  INVITE_ORGANIZATION_DIALOG_EMAILS_ADD,
  INVITE_ORGANIZATION_DIALOG_EMAILS_CHIP_REMOVE,
  INVITE_ORGANIZATION_DIALOG_EMAILS_INPUT,
  INVITE_ORGANIZATION_DIALOG_ROLE_ADMIN,
  INVITE_ORGANIZATION_DIALOG_ROLE_MEMBER,
} from 'src/ui/constants/testIds';
import { OrganizationRole } from 'src/ui/hooks/organization';
import * as copy from 'src/ui/templates/copy';
import { isValidEmail } from 'src/ui/utils/isValidEmail';

export interface InviteOrganizationSelectMembersStepProps {
  emails: string[];
  emailsError: string | null;
  emailsQuery: string;
  memberEmails: string[];
  onSetRole: React.Dispatch<
    React.SetStateAction<OrganizationRole.ADMIN | OrganizationRole.MEMBER>
  >;
  onSetEmails: React.Dispatch<React.SetStateAction<string[]>>;
  onSetEmailsError: React.Dispatch<React.SetStateAction<string | null>>;
  onSetEmailsQuery: React.Dispatch<React.SetStateAction<string>>;
  role: OrganizationRole.ADMIN | OrganizationRole.MEMBER;
}

export const InviteOrganizationSelectMembersStep: React.VFC<InviteOrganizationSelectMembersStepProps> = ({
  emails,
  emailsError,
  emailsQuery,
  memberEmails,
  onSetRole,
  onSetEmails,
  onSetEmailsError,
  onSetEmailsQuery,
  role,
}) => {
  const handleAddEmails = (newEmails: string[]) => {
    const valid: string[] = [];
    const allInvalid: string[] = [];
    const invalidExists: string[] = [];
    const invalidRegex: string[] = [];
    newEmails
      .map((email) => email.split(/\s*[\s,]\s*/))
      .flat()
      .forEach((email) => {
        if (!email) {
          return;
        }

        if (!isValidEmail(email)) {
          allInvalid.push(email);
          invalidRegex.push(email);
        } else if (memberEmails.includes(email)) {
          allInvalid.push(email);
          invalidExists.push(email);
        } else {
          valid.push(email);
        }
      });

    const invalidErrors: string[] = [];
    if (invalidExists.length) {
      invalidErrors.push(
        copy.components.organization.inviteDialog.selectMembers.email.validationExists(
          invalidExists
        )
      );
    }
    if (invalidRegex.length) {
      invalidErrors.push(
        copy.components.organization.dialog.inputs.email.validation(
          invalidRegex
        )
      );
    }
    onSetEmailsError(invalidErrors.length ? invalidErrors.join('\n') : null);

    if (valid.length) {
      onSetEmails((prev) => uniq([...prev, ...valid]));
    }

    onSetEmailsQuery(allInvalid.join(' '));
    return false;
  };

  return (
    <>
      <ChipInput
        chipButtonTestId={INVITE_ORGANIZATION_DIALOG_EMAILS_CHIP_REMOVE}
        className="organization-dialog-chip-input"
        clearable={false}
        helpProps={
          emailsError
            ? {
                className: 'organization-dialog-chip-input-error',
                text: emailsError,
              }
            : undefined
        }
        inputValue={emailsQuery}
        invalid={Boolean(emailsError)}
        label={
          copy.components.organization.inviteDialog.selectMembers.email.label
        }
        onAdd={handleAddEmails}
        onInputChange={(e) => onSetEmailsQuery(e.currentTarget.value)}
        onRemove={(_, idx) => {
          onSetEmails((prev) => {
            const next = [...prev];
            next.splice(idx, 1);
            return next;
          });
          onSetEmailsError(null);
        }}
        rightElement={
          emailsQuery.trim() ? (
            <InputButton
              icon={AddLarge}
              onClick={() => handleAddEmails([emailsQuery])}
              testId={INVITE_ORGANIZATION_DIALOG_EMAILS_ADD}
            />
          ) : undefined
        }
        size="md"
        testId={INVITE_ORGANIZATION_DIALOG_EMAILS_INPUT}
        values={emails}
      />

      <InputWrapper
        classNamePrefix="vb-input"
        label={
          copy.components.organization.inviteDialog.selectMembers.role.label
        }
        labelSize="md"
      >
        <>
          <Radio
            checked={role === OrganizationRole.ADMIN}
            onChange={() => onSetRole(OrganizationRole.ADMIN)}
            size="md"
            testId={INVITE_ORGANIZATION_DIALOG_ROLE_ADMIN}
          >
            {copy.components.organization.inviteDialog.selectMembers.role.admin}
          </Radio>
          <Radio
            checked={role === OrganizationRole.MEMBER}
            onChange={() => onSetRole(OrganizationRole.MEMBER)}
            size="md"
            testId={INVITE_ORGANIZATION_DIALOG_ROLE_MEMBER}
          >
            {
              copy.components.organization.inviteDialog.selectMembers.role
                .member
            }
          </Radio>
        </>
      </InputWrapper>
    </>
  );
};
